import { createHash } from 'node:crypto';
import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import { createVectorErrorId } from '@mastra/core/storage';
import { parseSqlIdentifier } from '@mastra/core/utils';
import { MastraVector, validateUpsertInput, validateTopK } from '@mastra/core/vector';
import type {
  IndexStats,
  QueryResult,
  QueryVectorParams,
  CreateIndexParams,
  UpsertVectorParams,
  DescribeIndexParams,
  DeleteIndexParams,
  DeleteVectorParams,
  DeleteVectorsParams,
  UpdateVectorParams,
} from '@mastra/core/vector';
import { Mutex } from 'async-mutex';
import * as pg from 'pg';
import xxhash from 'xxhash-wasm';

import { validateConfig, isCloudSqlConfig, isConnectionStringConfig, isHostConfig } from '../shared/config';
import type { PgVectorConfig } from '../shared/config';
import { buildConnectionStringPoolConfig } from '../shared/pool-config';
import { PGFilterTranslator } from './filter';
import type { PGVectorFilter } from './filter';
import { buildFilterQuery, buildDeleteFilterQuery } from './sql-builder';
import type { IndexConfig, IndexType, PgMetric, VectorOps, VectorType } from './types';
export type { PgMetric, VectorOps, VectorType, IndexConfig, IndexType } from './types';

export interface PGIndexStats extends IndexStats {
  type: IndexType;
  /**
   * The pgvector storage type used for this index.
   * - 'vector': Full precision (4 bytes per dimension)
   * - 'halfvec': Half precision (2 bytes per dimension)
   * - 'bit': Binary vectors (1 bit per dimension)
   * - 'sparsevec': Sparse vectors (only non-zero elements stored)
   */
  vectorType: VectorType;
  config: {
    m?: number;
    efConstruction?: number;
    lists?: number;
    probes?: number;
  };
}

/**
 * The subset of {@link PGIndexStats} that can be read from the Postgres catalog alone.
 *
 * Everything here comes from `information_schema.columns`, `pg_attribute` and `pg_index`,
 * which are cheap regardless of how large the table is. The row count is deliberately
 * excluded: it requires `SELECT COUNT(*)`, a full heap scan on large indexes.
 */
type PGIndexMetadata = Omit<PGIndexStats, 'count'>;

interface PgQueryVectorParams extends QueryVectorParams<PGVectorFilter> {
  namespace?: string;
  minScore?: number;
  /**
   * HNSW search parameter. Controls the size of the dynamic candidate
   * list during search. Higher values improve accuracy at the cost of speed.
   */
  ef?: number;
  /**
   * IVFFlat probe parameter. Number of cells to visit during search.
   * Higher values improve accuracy at the cost of speed.
   */
  probes?: number;
}

interface PgCreateIndexParams extends Omit<CreateIndexParams, 'metric'> {
  /**
   * Distance metric for the index.
   * Standard: 'cosine', 'euclidean', 'dotproduct' (work with all vector types)
   * Bit-specific: 'hamming' (count differing bits), 'jaccard' (1 - intersection/union)
   *
   * For 'bit' vectorType, defaults to 'hamming' if not specified.
   * 'jaccard' requires HNSW index (IVFFlat does not support Jaccard distance).
   */
  metric?: PgMetric;
  indexConfig?: IndexConfig;
  buildIndex?: boolean;
  /**
   * The pgvector storage type for embeddings.
   * - 'vector': Full precision (4 bytes per dimension), max 2000 dimensions for indexes (default)
   * - 'halfvec': Half precision (2 bytes per dimension), max 4000 dimensions for indexes
   * - 'bit': Binary vectors (1 bit per dimension), up to 64,000 dimensions for indexes
   * - 'sparsevec': Sparse vectors (only non-zero elements), up to 1,000 non-zero elements for indexes
   *
   * Use 'halfvec' for large dimension models like text-embedding-3-large (3072 dimensions)
   * Use 'bit' for binary quantization (reduced storage, faster search)
   * Use 'sparsevec' for BM25/TF-IDF and other sparse embeddings
   */
  vectorType?: VectorType;
  /**
   * Metadata fields to create btree indexes for.
   * This improves query performance when filtering vectors by these metadata fields.
   *
   * Each entry creates a btree index on `metadata->>'field_name'`.
   *
   * Example: `['thread_id', 'resource_id']` creates indexes that speed up
   * queries filtering by `thread_id` or `resource_id` in the metadata JSONB column.
   */
  metadataIndexes?: string[];
}

interface PgDefineIndexParams {
  indexName: string;
  metric: PgMetric;
  indexConfig: IndexConfig;
  vectorType?: VectorType;
}

type PgUpsertVectorParams = UpsertVectorParams<PGVectorFilter> & {
  namespace?: string;
};

type PgUpdateVectorParams = UpdateVectorParams<PGVectorFilter> & {
  namespace?: string;
};

type PgDeleteVectorParams = DeleteVectorParams & {
  namespace?: string;
};

type PgDeleteVectorsParams = DeleteVectorsParams<PGVectorFilter> & {
  namespace?: string;
};

const DEFAULT_NAMESPACE = 'default';

// Postgres allows at most 65535 bind parameters per statement; upserts bind 4 per row.
const MAX_UPSERT_ROWS_PER_STATEMENT = Math.floor(65535 / 4);

export class PgVector extends MastraVector<PGVectorFilter> {
  public pool: pg.Pool;
  /**
   * Cache for the public `getIndexInfo()`. Holds the in-flight promise rather than the
   * resolved value so concurrent callers on a cold cache share a single round trip.
   */
  private describeIndexCache: Map<string, Promise<PGIndexStats>> = new Map();
  /**
   * Cache for the catalog-only index metadata used by the internal hot paths
   * (cache warmup, query, upsert, updateVector, setupIndex). Also memoizes the promise.
   */
  private indexMetadataCache: Map<string, Promise<PGIndexMetadata>> = new Map();
  private createdIndexes = new Map<string, number>();
  private namespaceReadyIndexes = new Set<string>();
  /** In-flight lazy namespace migrations, keyed by index name (see {@link ensureNamespaceReady}). */
  private namespaceReadyCache: Map<string, Promise<void>> = new Map();
  private indexVectorTypes = new Map<string, VectorType>();
  private mutexesByName = new Map<string, Mutex>();
  private schema?: string;
  private setupSchemaPromise: Promise<void> | null = null;
  private installVectorExtensionPromise: Promise<void> | null = null;
  private vectorExtensionInstalled: boolean | undefined = undefined;
  private vectorExtensionSchema: string | null = null;
  private vectorExtensionVersion: string | null = null;
  private schemaSetupComplete: boolean | undefined = undefined;
  private cacheWarmupPromise: Promise<void> | null = null;

  constructor(config: PgVectorConfig & { id: string }) {
    try {
      validateConfig('PgVector', config);
      super({ id: config.id, disableInit: config.disableInit });

      this.schema = config.schemaName;

      let poolConfig: pg.PoolConfig;

      if (isConnectionStringConfig(config)) {
        // Delegate to the shared helper so an explicit `ssl` option wins over an
        // `sslmode=`/`ssl=` query param in the connection string, matching
        // PostgresStore. See https://github.com/mastra-ai/mastra/issues/17307
        poolConfig = {
          ...buildConnectionStringPoolConfig(config, { max: 20, idleTimeoutMillis: 30000 }),
          connectionTimeoutMillis: 2000,
          ...config.pgPoolOptions,
        };
      } else if (isCloudSqlConfig(config)) {
        poolConfig = {
          ...config,
          max: config.pgPoolOptions?.max ?? 20,
          idleTimeoutMillis: config.pgPoolOptions?.idleTimeoutMillis ?? 30000,
          connectionTimeoutMillis: 2000,
          ...config.pgPoolOptions,
        } as pg.PoolConfig;
      } else if (isHostConfig(config)) {
        poolConfig = {
          host: config.host,
          port: config.port,
          database: config.database,
          user: config.user,
          password: config.password,
          ssl: config.ssl,
          max: config.max ?? 20,
          idleTimeoutMillis: config.idleTimeoutMillis ?? 30000,
          connectionTimeoutMillis: 2000,
          ...config.pgPoolOptions,
        };
      } else {
        throw new Error('PgVector: invalid configuration provided');
      }

      this.pool = new pg.Pool(poolConfig);

      // pg emits 'error' on the pool when an idle client's connection drops
      // (backend restart, network partition, cloud proxies reaping idle
      // sockets). Without a listener Node escalates the event to an
      // uncaughtException and crashes the process. The pool already discards
      // the dead client, so warn and let the next checkout reconnect.
      this.pool.on('error', err => {
        this.logger?.warn?.(
          'PgVector: idle pool client error (pool discards the client and reconnects on next checkout)',
          {
            err: err instanceof Error ? err.message : err,
          },
        );
      });

      // Warm the created indexes cache in background so we don't need to check if indexes exist every time
      // Store the promise so we can wait for it during disconnect to avoid "pool already closed" errors
      this.cacheWarmupPromise = (async () => {
        try {
          const existingIndexes = await this.listIndexes();
          await Promise.all(
            existingIndexes.map(async indexName => {
              const info = await this.getIndexMetadata({ indexName });
              const key = await this.getIndexCacheKey({
                indexName,
                metric: info.metric,
                dimension: info.dimension,
                type: info.type,
                vectorType: info.vectorType,
              });
              this.createdIndexes.set(indexName, key);
              this.indexVectorTypes.set(indexName, info.vectorType);
            }),
          );
        } catch (error) {
          // Don't throw - cache warming is optional optimization
          // If it fails (e.g., pool closed early), just log and continue
          this.logger?.debug('Cache warming skipped or failed', { error });
        }
      })();
    } catch (error) {
      throw new MastraError(
        {
          id: createVectorErrorId('PG', 'INITIALIZATION', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            schemaName: 'schemaName' in config ? (config.schemaName ?? '') : '',
          },
        },
        error,
      );
    }
  }

  private getMutexByName(indexName: string) {
    if (!this.mutexesByName.has(indexName)) this.mutexesByName.set(indexName, new Mutex());
    return this.mutexesByName.get(indexName)!;
  }

  /**
   * Detects which schema contains the vector extension and its version
   */
  private async detectVectorExtensionSchema(client: pg.PoolClient): Promise<string | null> {
    try {
      const result = await client.query(`
        SELECT n.nspname as schema_name, e.extversion as version
        FROM pg_extension e
        JOIN pg_namespace n ON e.extnamespace = n.oid
        WHERE e.extname = 'vector'
        LIMIT 1;
      `);

      if (result.rows.length > 0) {
        this.vectorExtensionSchema = result.rows[0].schema_name;
        this.vectorExtensionVersion = result.rows[0].version;
        this.logger.debug('Vector extension found', {
          schema: this.vectorExtensionSchema,
          version: this.vectorExtensionVersion,
        });
        return this.vectorExtensionSchema;
      }

      return null;
    } catch (error) {
      this.logger.debug('Could not detect vector extension schema', { error });
      return null;
    }
  }

  /**
   * Sets search_path on the client connection so that vector operators (e.g. <=>, vector_cosine_ops)
   * are resolvable when the pgvector extension is installed in a non-default schema.
   *
   * PostgreSQL's default search_path is ("$user", public). If the extension lives in a custom schema
   * (e.g. "myapp"), operator classes and distance operators won't resolve without this.
   */
  private async ensureSearchPath(client: pg.PoolClient) {
    // Lazily detect extension schema if not yet known
    if (!this.vectorExtensionSchema) {
      await this.detectVectorExtensionSchema(client);
    }

    if (
      this.vectorExtensionSchema &&
      this.vectorExtensionSchema !== 'public' &&
      this.vectorExtensionSchema !== 'pg_catalog'
    ) {
      const schemas = new Set<string>();
      schemas.add(this.vectorExtensionSchema);
      if (this.schema) schemas.add(this.schema);
      schemas.add('public');
      await client.query(`SET search_path TO ${[...schemas].map(s => `"${s}"`).join(', ')}`);
    }
  }

  /**
   * Checks if the installed pgvector version supports halfvec type.
   * halfvec was introduced in pgvector 0.7.0.
   */
  private supportsHalfvec(): boolean {
    if (!this.vectorExtensionVersion) {
      return false;
    }
    // Parse version string, handling non-numeric suffixes (e.g., "0.7.0-beta", "0.8.0+build")
    const parts = this.vectorExtensionVersion.split('.');
    const major = parseInt(parts[0] ?? '', 10);
    const minor = parseInt(parts[1] ?? '', 10);
    // If parsing failed (NaN), assume version doesn't support halfvec
    if (isNaN(major) || isNaN(minor)) {
      return false;
    }
    // halfvec was introduced in pgvector 0.7.0
    return major > 0 || (major === 0 && minor >= 7);
  }

  /** Checks if pgvector >= 0.7.0 (required for bit type). */
  private supportsBit(): boolean {
    return this.supportsHalfvec();
  }

  /** Checks if pgvector >= 0.7.0 (required for sparsevec type). */
  private supportsSparsevec(): boolean {
    return this.supportsHalfvec();
  }

  /**
   * Gets the properly qualified vector type name
   * @param vectorType - The type of vector storage
   */
  private getVectorTypeName(vectorType: VectorType = 'vector', dimension?: number): string {
    // 'bit' is a native PostgreSQL type (in pg_catalog), not a pgvector extension type.
    // It must never be schema-qualified with the extension schema.
    // When dimension is provided, return 'bit(N)' for proper casting (PostgreSQL treats bare 'bit' as 'bit(1)').
    if (vectorType === 'bit') {
      return dimension ? `bit(${dimension})` : 'bit';
    }

    // If we know where the extension is, use that
    if (this.vectorExtensionSchema) {
      // If it's in pg_catalog, return the type directly
      if (this.vectorExtensionSchema === 'pg_catalog') {
        return vectorType;
      }
      // Issue #10061: Always qualify with schema where vector extension is installed
      // This ensures the type is found regardless of the session's search_path
      const validatedSchema = parseSqlIdentifier(this.vectorExtensionSchema, 'vector extension schema');
      return `${validatedSchema}.${vectorType}`;
    }

    // Fallback to unqualified (will use search_path)
    return vectorType;
  }

  /**
   * Returns the operator class, distance operator, and score expression for a
   * standard (non-bit) vector type prefix and metric.
   */
  private getMetricOps(
    prefix: string,
    metric: PgMetric,
  ): Pick<VectorOps, 'operatorClass' | 'distanceOperator' | 'scoreExpr'> {
    switch (metric) {
      case 'euclidean':
        return {
          operatorClass: `${prefix}_l2_ops`,
          distanceOperator: '<->',
          scoreExpr: d => `1.0 / (1.0 + (${d}))`,
        };
      case 'dotproduct':
        return {
          operatorClass: `${prefix}_ip_ops`,
          distanceOperator: '<#>',
          scoreExpr: d => `(${d}) * -1`,
        };
      default:
        return {
          operatorClass: `${prefix}_cosine_ops`,
          distanceOperator: '<=>',
          scoreExpr: d => `1 - (${d})`,
        };
    }
  }

  /**
   * Returns all vector-type-specific operations for the given vectorType and metric.
   */
  private getVectorOps(vectorType: VectorType, metric: PgMetric): VectorOps {
    switch (vectorType) {
      case 'bit':
        return {
          operatorClass: metric === 'jaccard' ? 'bit_jaccard_ops' : 'bit_hamming_ops',
          distanceOperator: metric === 'jaccard' ? '<%>' : '<~>',
          scoreExpr: d => (metric === 'jaccard' ? `1 - (${d})` : `1 - ((${d})::float / bit_length(embedding))`),
          formatVector: v => v.map(x => (x ? '1' : '0')).join(''),
          parseEmbedding: e => e.split('').map(c => (c === '1' ? 1 : 0)),
        };

      case 'sparsevec':
        return {
          ...this.getMetricOps('sparsevec', metric),
          formatVector: (v, dimension) => {
            const dim = dimension ?? v.length;
            const nonZero = v
              .map((val, i) => (val !== 0 ? `${i + 1}:${val}` : null))
              .filter(Boolean)
              .join(',');
            return `{${nonZero}}/${dim}`;
          },
          parseEmbedding: e => {
            const match = e.match(/^\{([^}]*)\}\/(\d+)$/);
            if (!match) return [];
            const dim = parseInt(match[2]!, 10);
            const result = new Array(dim).fill(0) as number[];
            const entries = match[1]!;
            if (entries.trim()) {
              for (const entry of entries.split(',')) {
                const [idxStr, valStr] = entry.split(':');
                const idx = parseInt(idxStr!.trim(), 10) - 1;
                result[idx] = parseFloat(valStr!.trim());
              }
            }
            return result;
          },
        };

      case 'halfvec':
      case 'vector':
      default:
        return {
          ...this.getMetricOps(vectorType === 'halfvec' ? 'halfvec' : 'vector', metric),
          formatVector: v => `[${v.join(',')}]`,
          parseEmbedding: e => JSON.parse(e),
        };
    }
  }

  private getTableName(indexName: string) {
    const parsedIndexName = parseSqlIdentifier(indexName, 'index name');
    const quotedIndexName = `"${parsedIndexName}"`;
    const quotedSchemaName = this.getSchemaName();
    const quotedVectorName = `"${parsedIndexName}_vector_idx"`;
    return {
      tableName: quotedSchemaName ? `${quotedSchemaName}.${quotedIndexName}` : quotedIndexName,
      vectorIndexName: quotedVectorName,
      parsedIndexName,
    };
  }

  private getSchemaName() {
    return this.schema ? `"${parseSqlIdentifier(this.schema, 'schema name')}"` : undefined;
  }

  private async ensureNamespaceSchema(indexName: string, client: pg.PoolClient): Promise<void> {
    const { tableName, parsedIndexName } = this.getTableName(indexName);
    // Anchor on to_regclass(tableName) so the lookup resolves the same relation as the
    // unqualified DDL does (via search_path) when no schemaName is configured.
    const vectorIdColumn = await client.query(
      `SELECT 1
       FROM pg_attribute
       WHERE attrelid = to_regclass($1)
         AND attname = 'vector_id'
         AND attnum > 0
         AND NOT attisdropped`,
      [tableName],
    );
    if (vectorIdColumn.rowCount === 0) {
      return;
    }

    await client.query(
      `ALTER TABLE ${tableName} ADD COLUMN IF NOT EXISTS namespace VARCHAR(255) NOT NULL DEFAULT '${DEFAULT_NAMESPACE}'`,
    );

    const state = await this.getNamespaceSchemaState(tableName, client);
    if (!state.composite_index) {
      const namespaceIndexName = this.getNamespaceIndexName(parsedIndexName);
      await client.query(
        `CREATE UNIQUE INDEX IF NOT EXISTS "${namespaceIndexName}" ON ${tableName} (namespace, vector_id)`,
      );
    }

    const legacyConstraints = await client.query<{ conname: string }>(
      `SELECT c.conname
       FROM pg_constraint c
       WHERE c.conrelid = to_regclass($1)
         AND c.contype = 'u'
         AND pg_get_constraintdef(c.oid) = 'UNIQUE (vector_id)'`,
      [tableName],
    );

    for (const { conname } of legacyConstraints.rows) {
      const parsedConstraintName = parseSqlIdentifier(conname, 'constraint name');
      await client.query(`ALTER TABLE ${tableName} DROP CONSTRAINT "${parsedConstraintName}"`);
    }
  }

  private getNamespaceIndexName(parsedIndexName: string): string {
    const fullName = `${parsedIndexName}_namespace_vector_id_idx`;
    if (fullName.length <= 63) {
      return fullName;
    }
    const hash = createHash('sha256').update(parsedIndexName).digest('hex').slice(0, 32);
    const suffix = `_ns_${hash}_idx`;
    return `${parsedIndexName.slice(0, 63 - suffix.length)}${suffix}`;
  }

  private async getNamespaceSchemaState(tableName: string, client: pg.PoolClient) {
    const result = await client.query<{
      vector_id: boolean;
      namespace: boolean;
      composite_index: boolean;
      legacy_constraint: boolean;
    }>(
      `SELECT
         EXISTS (SELECT 1 FROM pg_attribute WHERE attrelid = to_regclass($1)
           AND attname = 'vector_id' AND attnum > 0 AND NOT attisdropped) AS vector_id,
         EXISTS (SELECT 1 FROM pg_attribute WHERE attrelid = to_regclass($1)
           AND attname = 'namespace' AND attnum > 0 AND NOT attisdropped) AS namespace,
         EXISTS (
           SELECT 1 FROM pg_index i
           WHERE i.indrelid = to_regclass($1)
             AND i.indisunique AND i.indisvalid AND i.indisready AND i.indimmediate
             AND i.indpred IS NULL AND i.indexprs IS NULL AND i.indnkeyatts = 2
             AND (SELECT array_agg(a.attname::text ORDER BY a.attname)
                  FROM unnest(i.indkey) WITH ORDINALITY AS k(attnum, position)
                  JOIN pg_attribute a ON a.attrelid = i.indrelid AND a.attnum = k.attnum
                  WHERE k.position <= i.indnkeyatts) = ARRAY['namespace', 'vector_id']
         ) AS composite_index,
         EXISTS (SELECT 1 FROM pg_constraint WHERE conrelid = to_regclass($1)
           AND contype = 'u' AND pg_get_constraintdef(oid) = 'UNIQUE (vector_id)') AS legacy_constraint`,
      [tableName],
    );
    return result.rows[0]!;
  }

  private async reconcileNamespace(indexName: string, client: pg.PoolClient): Promise<boolean> {
    const { tableName } = this.getTableName(indexName);
    await client.query('BEGIN');
    try {
      // Resolve aliases (explicit schema and search_path) to the same cross-process lock.
      await client.query('SELECT pg_advisory_xact_lock((1936876916::bigint << 32) | to_regclass($1)::oid::bigint)', [
        tableName,
      ]);
      const state = await this.getNamespaceSchemaState(tableName, client);
      if (!state.vector_id) {
        await client.query('ROLLBACK');
        return false;
      }
      if (!state.namespace || !state.composite_index || state.legacy_constraint) {
        if (this.disableInit || process.env.MASTRA_DISABLE_STORAGE_INIT === 'true') {
          throw new MastraError({
            id: createVectorErrorId('PG', 'ENSURE_NAMESPACE', 'MIGRATION_REQUIRED'),
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            text:
              `Vector index "${indexName}" requires namespace migration and schema changes are disabled. ` +
              `Call createIndex({ indexName: "${indexName}", dimension }) with init enabled, ` +
              `or apply the namespace migration SQL from the @mastra/pg changelog.`,
            details: { indexName },
          });
        }
        await this.ensureNamespaceSchema(indexName, client);
        const migrated = await this.getNamespaceSchemaState(tableName, client);
        if (!migrated.namespace || !migrated.composite_index || migrated.legacy_constraint) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'ENSURE_NAMESPACE', 'MIGRATION_REQUIRED'),
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            text: `Vector index "${indexName}" requires a valid unique (namespace, vector_id) index. Resolve conflicting index names and retry the namespace migration.`,
            details: { indexName },
          });
        }
      }
      await client.query('COMMIT');
      return true;
    } catch (error) {
      await client.query('ROLLBACK');
      throw error;
    }
  }

  /**
   * Every data path filters on `namespace`, but tables created before that column existed are
   * only migrated by `createIndex()`, which callers are not required to invoke before reading.
   * Run the migration lazily instead, once per index per process.
   */
  private ensureNamespaceReady(indexName: string): Promise<void> {
    if (this.namespaceReadyIndexes.has(indexName)) {
      return Promise.resolve();
    }

    return this.memoize(this.namespaceReadyCache, indexName, () =>
      // Share the createIndex mutex so lazy migration never races its DDL on the same table.
      this.getMutexByName(`create-${indexName}`).runExclusive(async () => {
        if (this.namespaceReadyIndexes.has(indexName)) {
          return;
        }

        const client = await this.pool.connect();
        try {
          if (await this.reconcileNamespace(indexName, client)) {
            this.namespaceReadyIndexes.add(indexName);
          } else {
            // Missing/non-vector tables must be checked again after external provisioning.
            this.namespaceReadyCache.delete(indexName);
          }
        } finally {
          client.release();
        }
      }),
    );
  }

  transformFilter(filter?: PGVectorFilter) {
    const translator = new PGFilterTranslator();
    return translator.translate(filter);
  }

  /**
   * Cached variant of {@link describeIndex}, including the row count.
   *
   * Internal code paths do not use this - they use the catalog-only
   * {@link getIndexMetadata}, which never scans the table.
   */
  async getIndexInfo({ indexName }: DescribeIndexParams): Promise<PGIndexStats> {
    return this.memoize(this.describeIndexCache, indexName, () => this.describeIndex({ indexName }));
  }

  /**
   * Cached index metadata read from the Postgres catalog only.
   *
   * This is what every internal caller needs: none of them read `count`, and paying for
   * `SELECT COUNT(*)` on a large index costs a full heap scan per call.
   */
  private getIndexMetadata({ indexName }: DescribeIndexParams): Promise<PGIndexMetadata> {
    return this.memoize(this.indexMetadataCache, indexName, () => this.describeIndexMetadata({ indexName }));
  }

  /**
   * Stores the in-flight promise in `cache` so concurrent callers on a cold cache share one
   * round trip, and drops the entry if it rejects so a transient failure is not cached forever.
   */
  private memoize<T>(cache: Map<string, Promise<T>>, key: string, load: () => Promise<T>): Promise<T> {
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const pending = load();
    cache.set(key, pending);
    pending.catch(() => {
      if (cache.get(key) === pending) {
        cache.delete(key);
      }
    });
    return pending;
  }

  /** Drops every cached view of an index, e.g. after its index definition or table changed. */
  private invalidateIndexCaches(indexName: string) {
    this.describeIndexCache.delete(indexName);
    this.indexMetadataCache.delete(indexName);
  }

  async query({
    indexName,
    queryVector,
    topK = 10,
    filter,
    includeVector = false,
    minScore = -1,
    ef,
    probes,
    namespace = DEFAULT_NAMESPACE,
  }: PgQueryVectorParams): Promise<QueryResult[]> {
    try {
      // Validate topK parameter
      validateTopK('PG', topK);
      if (queryVector !== undefined) {
        if (!Array.isArray(queryVector) || !queryVector.every(x => typeof x === 'number' && Number.isFinite(x))) {
          throw new Error('queryVector must be an array of finite numbers');
        }
      } else if (!filter || Object.keys(filter).length === 0) {
        throw new Error('Either queryVector or filter must be provided');
      }
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'QUERY', 'INVALID_INPUT'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: {
            indexName,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    }

    // Metadata-only query: filter without vector similarity
    if (queryVector === undefined) {
      let client;
      try {
        await this.ensureNamespaceReady(indexName);
        client = await this.pool.connect();
        const translatedFilter = this.transformFilter(filter);
        const { sql: filterQuery, values: filterValues } = buildDeleteFilterQuery(translatedFilter);
        const { tableName } = this.getTableName(indexName);

        const filterClause = filterQuery.trim().replace(/^WHERE\s+/i, '');
        const namespaceParam = filterValues.length + 1;
        const query = `
          SELECT
            vector_id as id,
            metadata
            ${includeVector ? ', embedding' : ''}
          FROM ${tableName}
          WHERE namespace = $${namespaceParam}
          ${filterClause ? `AND (${filterClause})` : ''}
          ORDER BY vector_id
          LIMIT $${namespaceParam + 1}`;
        const result = await client.query(query, [...filterValues, namespace, topK]);

        return result.rows.map(({ id, metadata, embedding }: { id: string; metadata: any; embedding?: string }) => ({
          id,
          score: 0,
          metadata,
          ...(includeVector && embedding && { vector: JSON.parse(embedding) }),
        }));
      } catch (error) {
        if (error instanceof MastraError) {
          this.logger?.trackException(error);
          throw error;
        }
        const mastraError = new MastraError(
          {
            id: createVectorErrorId('PG', 'QUERY', 'FAILED'),
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.THIRD_PARTY,
            details: {
              indexName,
            },
          },
          error,
        );
        this.logger?.trackException(mastraError);
        throw mastraError;
      } finally {
        client?.release();
      }
    }

    let client;
    try {
      await this.ensureNamespaceReady(indexName);
      // Load metadata before holding a connection so a cold cache cannot exhaust the pool.
      const indexInfo = await this.getIndexMetadata({ indexName });
      // Vector similarity query
      client = await this.pool.connect();
      // Set search path so vector operators (e.g. <=>) resolve correctly
      await this.ensureSearchPath(client);
      await client.query('BEGIN');
      const translatedFilter = this.transformFilter(filter);
      const { sql: filterQuery, values: filterValues } = buildFilterQuery(translatedFilter, minScore, topK);

      const metric = indexInfo.metric ?? 'cosine';
      const ops = this.getVectorOps(indexInfo.vectorType, metric);

      // Format vector string based on vector type
      const vectorStr = ops.formatVector(queryVector, indexInfo.dimension);

      // Set HNSW search parameter if applicable
      if (indexInfo.type === 'hnsw') {
        // Calculate ef and clamp between 1 and 1000
        const calculatedEf = ef ?? Math.max(topK, (indexInfo?.config?.m ?? 16) * topK);
        const searchEf = Math.min(1000, Math.max(1, calculatedEf));
        await client.query(`SET LOCAL hnsw.ef_search = ${searchEf}`);
        await client.query(`SET LOCAL hnsw.iterative_scan = strict_order`);
      }

      if (indexInfo.type === 'ivfflat' && probes) {
        await client.query(`SET LOCAL ivfflat.probes = ${probes}`);
      }

      const { tableName } = this.getTableName(indexName);

      // Get the properly qualified vector type based on the index's vector type
      const qualifiedVectorType = this.getVectorTypeName(indexInfo.vectorType, indexInfo.dimension);

      // Build distance expression and score based on metric and vector type
      const distanceExpr = `embedding ${ops.distanceOperator} '${vectorStr}'::${qualifiedVectorType}`;
      const scoreExpr = ops.scoreExpr(distanceExpr);

      // Move ORDER BY and LIMIT inside the CTE for HNSW indexes without filters to enable index usage.
      // Only safe when minScore won't filter out candidates (minScore <= 0), otherwise the inner LIMIT
      // cuts off the candidate set before the score threshold is applied, potentially returning fewer rows.
      // IVFFlat is excluded because with default probes=1, it only searches one cluster and can miss
      // vectors in other clusters, returning fewer results than expected.
      const filterClause = filterQuery.trim().replace(/^WHERE\s+/i, '');
      const hasFilter = filterClause.length > 0;
      const useIndexedOrder = indexInfo.type === 'hnsw' && !hasFilter && minScore <= 0;
      const namespaceParam = filterValues.length + 1;

      const query = useIndexedOrder
        ? `
        WITH vector_scores AS (
          SELECT
            vector_id as id,
            ${scoreExpr} as score,
            metadata
            ${includeVector ? ', embedding' : ''}
          FROM ${tableName}
          WHERE namespace = $${namespaceParam}
          ORDER BY ${distanceExpr}
          LIMIT $2
        )
        SELECT *
        FROM vector_scores
        WHERE score > $1
        ORDER BY score DESC`
        : `
        WITH vector_scores AS (
          SELECT
            vector_id as id,
            ${scoreExpr} as score,
            metadata
            ${includeVector ? ', embedding' : ''}
          FROM ${tableName}
          WHERE namespace = $${namespaceParam}
          ${filterClause ? `AND (${filterClause})` : ''}
        )
        SELECT *
        FROM vector_scores
        WHERE score > $1
        ORDER BY score DESC
        LIMIT $2`;
      const result = await client.query(query, [...filterValues, namespace]);
      await client.query('COMMIT');

      return result.rows.map(({ id, score, metadata, embedding }) => ({
        id,
        score,
        metadata,
        ...(includeVector && embedding && { vector: ops.parseEmbedding(embedding) }),
      }));
    } catch (error) {
      await client?.query('ROLLBACK');
      if (error instanceof MastraError) {
        this.logger?.trackException(error);
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'QUERY', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client?.release();
    }
  }

  async upsert({
    indexName,
    vectors,
    metadata,
    ids,
    deleteFilter,
    namespace = DEFAULT_NAMESPACE,
  }: PgUpsertVectorParams): Promise<string[]> {
    // Validate input parameters
    validateUpsertInput('PG', vectors, metadata, ids);

    const { tableName } = this.getTableName(indexName);

    let client;
    try {
      const indexInfo = await this.getIndexMetadata({ indexName });
      await this.ensureNamespaceReady(indexName);

      // Start a transaction
      client = await this.pool.connect();
      // Set search path so vector type casts (e.g. ::vector, ::halfvec) resolve correctly
      await this.ensureSearchPath(client);

      await client.query('BEGIN');

      // Step 1: If deleteFilter is provided, delete matching vectors first
      if (deleteFilter) {
        this.logger?.debug(`Deleting vectors matching filter before upsert`, { indexName, deleteFilter });

        // Reuse the filter translation logic
        const translatedFilter = this.transformFilter(deleteFilter);
        const { sql: filterQuery, values: filterValues } = buildDeleteFilterQuery(translatedFilter);

        const whereClause = filterQuery.trim().replace(/^WHERE\s+/i, '');
        if (whereClause) {
          const deleteQuery = `DELETE FROM ${tableName} WHERE namespace = $${filterValues.length + 1} AND (${whereClause})`;
          const result = await client.query(deleteQuery, [...filterValues, namespace]);
          this.logger?.debug(`Deleted ${result.rowCount || 0} vectors before upsert`, {
            indexName,
            deletedCount: result.rowCount || 0,
          });
        }
      }

      // Step 2: Insert/update new vectors
      const vectorIds = ids || vectors.map(() => crypto.randomUUID());

      // Get the properly qualified vector type for this index
      const qualifiedVectorType = this.getVectorTypeName(indexInfo.vectorType, indexInfo.dimension);
      const ops = this.getVectorOps(indexInfo.vectorType, indexInfo.metric ?? 'cosine');

      // Postgres multi-row ON CONFLICT DO UPDATE cannot affect the same row twice, so batches
      // containing duplicate ids keep the serial path (last write wins).
      const hasDuplicateIds = new Set(vectorIds).size !== vectorIds.length;

      if (hasDuplicateIds) {
        for (let i = 0; i < vectors.length; i++) {
          const vectorStr = ops.formatVector(vectors[i]!, indexInfo.dimension);
          const query = `
            INSERT INTO ${tableName} (vector_id, embedding, metadata, namespace)
            VALUES ($1, $2::${qualifiedVectorType}, $3::jsonb, $4)
            ON CONFLICT (namespace, vector_id)
            DO UPDATE SET
              embedding = $2::${qualifiedVectorType},
              metadata = $3::jsonb
          `;

          await client.query(query, [vectorIds[i], vectorStr, JSON.stringify(metadata?.[i] || {}), namespace]);
        }
      } else {
        for (let start = 0; start < vectors.length; start += MAX_UPSERT_ROWS_PER_STATEMENT) {
          const end = Math.min(start + MAX_UPSERT_ROWS_PER_STATEMENT, vectors.length);
          const rows: string[] = [];
          const values: any[] = [];

          for (let i = start; i < end; i++) {
            const base = values.length;
            rows.push(`($${base + 1}, $${base + 2}::${qualifiedVectorType}, $${base + 3}::jsonb, $${base + 4})`);
            values.push(
              vectorIds[i],
              ops.formatVector(vectors[i]!, indexInfo.dimension),
              JSON.stringify(metadata?.[i] || {}),
              namespace,
            );
          }

          const query = `
            INSERT INTO ${tableName} (vector_id, embedding, metadata, namespace)
            VALUES ${rows.join(', ')}
            ON CONFLICT (namespace, vector_id)
            DO UPDATE SET
              embedding = EXCLUDED.embedding,
              metadata = EXCLUDED.metadata
          `;

          await client.query(query, values);
        }
      }

      await client.query('COMMIT');

      this.logger?.debug(`Upserted ${vectors.length} vectors to ${indexName}`, {
        indexName,
        vectorCount: vectors.length,
        hadDeleteFilter: !!deleteFilter,
      });

      return vectorIds;
    } catch (error) {
      await client?.query('ROLLBACK');
      if (error instanceof MastraError) {
        this.logger?.trackException(error);
        throw error;
      }
      if (error instanceof Error && error.message?.includes('expected') && error.message?.includes('dimensions')) {
        const match = error.message.match(/expected (\d+) dimensions, not (\d+)/);
        if (match) {
          const [, expected, actual] = match;
          const mastraError = new MastraError(
            {
              id: createVectorErrorId('PG', 'UPSERT', 'INVALID_INPUT'),
              domain: ErrorDomain.MASTRA_VECTOR,
              category: ErrorCategory.USER,
              text:
                `Vector dimension mismatch: Index "${indexName}" expects ${expected} dimensions but got ${actual} dimensions. ` +
                `Either use a matching embedding model or delete and recreate the index with the new dimension.`,
              details: {
                indexName,
                expected: expected ?? '',
                actual: actual ?? '',
              },
            },
            error,
          );
          this.logger?.trackException(mastraError);
          throw mastraError;
        }
      }

      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'UPSERT', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client?.release();
    }
  }

  private hasher = xxhash();
  private async getIndexCacheKey({
    indexName,
    dimension,
    metric,
    type,
    vectorType = 'vector',
    metadataIndexes,
  }: Omit<CreateIndexParams, 'metric'> & {
    metric?: PgMetric;
    type: IndexType | undefined;
    vectorType?: VectorType;
    metadataIndexes?: string[];
  }) {
    const input = JSON.stringify([
      indexName,
      dimension,
      metric,
      type || 'ivfflat',
      vectorType,
      metadataIndexes?.toSorted() ?? [],
    ]);
    return (await this.hasher).h32(input);
  }
  private cachedIndexExists(indexName: string, newKey: number) {
    const existingIndexCacheKey = this.createdIndexes.get(indexName);
    return existingIndexCacheKey && existingIndexCacheKey === newKey;
  }
  private async setupSchema(client: pg.PoolClient) {
    if (!this.schema || this.schemaSetupComplete) {
      return;
    }

    if (!this.setupSchemaPromise) {
      this.setupSchemaPromise = (async () => {
        try {
          // First check if schema exists and we have usage permission
          const schemaCheck = await client.query(
            `
            SELECT EXISTS (
              SELECT 1 FROM information_schema.schemata
              WHERE schema_name = $1
            )
          `,
            [this.schema],
          );

          const schemaExists = schemaCheck.rows[0].exists;

          if (!schemaExists) {
            try {
              await client.query(`CREATE SCHEMA IF NOT EXISTS ${this.getSchemaName()}`);
              this.logger.info(`Schema "${this.schema}" created successfully`);
            } catch (error) {
              this.logger.error(`Failed to create schema "${this.schema}"`, { error });
              throw new Error(
                `Unable to create schema "${this.schema}". This requires CREATE privilege on the database. ` +
                  `Either create the schema manually or grant CREATE privilege to the user.`,
              );
            }
          }

          // If we got here, schema exists and we can use it
          this.schemaSetupComplete = true;
          this.logger.debug(`Schema "${this.schema}" is ready for use`);
        } catch (error) {
          // Reset flags so we can retry
          this.schemaSetupComplete = undefined;
          this.setupSchemaPromise = null;
          throw error;
        } finally {
          this.setupSchemaPromise = null;
        }
      })();
    }

    await this.setupSchemaPromise;
  }

  async createIndex({
    indexName,
    dimension,
    metric: rawMetric = 'cosine',
    indexConfig = {},
    buildIndex = true,
    vectorType = 'vector',
    metadataIndexes,
  }: PgCreateIndexParams): Promise<void> {
    // Normalize metric for bit vectors: default to 'hamming' unless explicitly 'hamming' or 'jaccard'
    const metric: PgMetric =
      vectorType === 'bit' && rawMetric !== 'hamming' && rawMetric !== 'jaccard' ? 'hamming' : rawMetric;

    const { tableName } = this.getTableName(indexName);

    // Validate inputs
    try {
      if (!indexName.match(/^[a-zA-Z_][a-zA-Z0-9_]*$/)) {
        throw new Error('Invalid index name format');
      }
      if (!Number.isInteger(dimension) || dimension <= 0) {
        throw new Error('Dimension must be a positive integer');
      }
      if (vectorType !== 'vector' && vectorType !== 'halfvec' && vectorType !== 'bit' && vectorType !== 'sparsevec') {
        throw new Error('vectorType must be "vector", "halfvec", "bit", or "sparsevec"');
      }
      // Dimension limits for indexed vectors (pgvector restrictions)
      if (vectorType === 'bit' && dimension > 64000) {
        throw new Error('bit vectors support up to 64,000 dimensions for indexes');
      }

      // hamming and jaccard metrics are only valid with bit vectors
      if ((metric === 'hamming' || metric === 'jaccard') && vectorType !== 'bit') {
        throw new Error(`${metric} metric is only valid with vectorType 'bit'`);
      }
      // IVFFlat does not support Jaccard distance for bit vectors
      if (indexConfig?.type === 'ivfflat' && vectorType === 'bit' && metric === 'jaccard') {
        throw new Error('IVFFlat indexes do not support Jaccard distance for bit vectors. Use HNSW instead.');
      }
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'CREATE_INDEX', 'INVALID_INPUT'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: {
            indexName,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    }

    // Skip all DDL when init is disabled - caller manages schema/extension/tables/indexes externally
    if (this.disableInit || process.env.MASTRA_DISABLE_STORAGE_INIT === 'true') {
      return;
    }

    const indexCacheKey = await this.getIndexCacheKey({
      indexName,
      dimension,
      type: indexConfig.type,
      metric,
      vectorType,
      metadataIndexes,
    });
    if (this.cachedIndexExists(indexName, indexCacheKey) && this.namespaceReadyIndexes.has(indexName)) {
      // we already saw this index get created and reconciled since the process started
      return;
    }

    const mutex = this.getMutexByName(`create-${indexName}`);
    // Use async-mutex instead of advisory lock for perf (over 2x as fast)
    await mutex
      .runExclusive(async () => {
        if (this.cachedIndexExists(indexName, indexCacheKey) && this.namespaceReadyIndexes.has(indexName)) {
          // this may have been created and reconciled while we were waiting to acquire a lock
          return;
        }

        const client = await this.pool.connect();

        try {
          // Setup schema if needed
          await this.setupSchema(client);

          // Install vector extension and detect where it is
          await this.installVectorExtension(client);

          // Check if halfvec is supported when requested
          if (vectorType === 'halfvec' && !this.supportsHalfvec()) {
            throw new MastraError({
              id: createVectorErrorId('PG', 'CREATE_INDEX', 'HALFVEC_NOT_SUPPORTED'),
              text:
                `halfvec type requires pgvector >= 0.7.0, but version ${this.vectorExtensionVersion || 'unknown'} is installed. ` +
                `Either upgrade pgvector or use vectorType: 'vector' (which supports up to 2000 dimensions for indexes).`,
              domain: ErrorDomain.MASTRA_VECTOR,
              category: ErrorCategory.USER,
              details: {
                indexName,
                requestedVectorType: vectorType,
                pgvectorVersion: this.vectorExtensionVersion || 'unknown',
                requiredVersion: '0.7.0',
              },
            });
          }

          // Check if bit/sparsevec is supported when requested
          if (vectorType === 'bit' && !this.supportsBit()) {
            throw new MastraError({
              id: createVectorErrorId('PG', 'CREATE_INDEX', 'VECTOR_TYPE_NOT_SUPPORTED'),
              text:
                `${vectorType} type requires pgvector >= 0.7.0, but version ${this.vectorExtensionVersion || 'unknown'} is installed. ` +
                `Either upgrade pgvector or use vectorType: 'vector'.`,
              domain: ErrorDomain.MASTRA_VECTOR,
              category: ErrorCategory.USER,
              details: {
                indexName,
                vectorType,
                installedVersion: this.vectorExtensionVersion,
              },
            });
          }

          if (vectorType === 'sparsevec' && !this.supportsSparsevec()) {
            throw new MastraError({
              id: createVectorErrorId('PG', 'CREATE_INDEX', 'VECTOR_TYPE_NOT_SUPPORTED'),
              text:
                `${vectorType} type requires pgvector >= 0.7.0, but version ${this.vectorExtensionVersion || 'unknown'} is installed. ` +
                `Either upgrade pgvector or use vectorType: 'vector'.`,
              domain: ErrorDomain.MASTRA_VECTOR,
              category: ErrorCategory.USER,
              details: {
                indexName,
                requestedVectorType: vectorType,
                pgvectorVersion: this.vectorExtensionVersion || 'unknown',
                requiredVersion: '0.7.0',
              },
            });
          }

          // Use the properly qualified vector type (vector or halfvec)
          const qualifiedVectorType = this.getVectorTypeName(vectorType);

          await client.query(`
          CREATE TABLE IF NOT EXISTS ${tableName} (
            id SERIAL PRIMARY KEY,
            vector_id TEXT NOT NULL,
            embedding ${qualifiedVectorType}(${dimension}),
            metadata JSONB DEFAULT '{}'::jsonb,
            namespace VARCHAR(255) NOT NULL DEFAULT '${DEFAULT_NAMESPACE}'
          );
        `);
          if (await this.reconcileNamespace(indexName, client)) {
            this.namespaceReadyIndexes.add(indexName);
          }
          this.createdIndexes.set(indexName, indexCacheKey);
          this.indexVectorTypes.set(indexName, vectorType);

          if (buildIndex) {
            await this.setupIndex({ indexName, metric, indexConfig, vectorType }, client);
          }

          if (metadataIndexes?.length) {
            await this.createMetadataIndexes(tableName, indexName, metadataIndexes);
          }
        } catch (error: any) {
          this.createdIndexes.delete(indexName);
          this.namespaceReadyIndexes.delete(indexName);
          this.namespaceReadyCache.delete(indexName);
          this.indexVectorTypes.delete(indexName);
          throw error;
        } finally {
          client.release();
        }
      })
      .catch(error => {
        const mastraError = new MastraError(
          {
            id: createVectorErrorId('PG', 'CREATE_INDEX', 'FAILED'),
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.THIRD_PARTY,
            details: {
              indexName,
            },
          },
          error,
        );
        this.logger?.trackException(mastraError);
        throw mastraError;
      });
  }

  async buildIndex({ indexName, metric = 'cosine', indexConfig, vectorType }: PgDefineIndexParams): Promise<void> {
    const client = await this.pool.connect();
    try {
      await this.setupIndex({ indexName, metric, indexConfig, vectorType }, client);
    } catch (error: any) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'BUILD_INDEX', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client.release();
    }
  }

  private async setupIndex(
    { indexName, metric, indexConfig, vectorType = 'vector' }: PgDefineIndexParams,
    client: pg.PoolClient,
  ) {
    const mutex = this.getMutexByName(`build-${indexName}`);
    // Use async-mutex instead of advisory lock for perf (over 2x as fast)
    await mutex.runExclusive(async () => {
      // Check if the index config is empty
      const isConfigEmpty =
        !indexConfig ||
        Object.keys(indexConfig).length === 0 ||
        (!indexConfig.type && !indexConfig.ivf && !indexConfig.hnsw);
      // Determine index type - use defaults if no config provided
      // sparsevec does not support IVFFlat, so default to HNSW for it
      const defaultIndexType = vectorType === 'sparsevec' ? 'hnsw' : 'ivfflat';
      const indexType = isConfigEmpty ? defaultIndexType : indexConfig.type || defaultIndexType;

      // Validate index type restrictions for sparsevec
      if (indexType === 'ivfflat' && vectorType === 'sparsevec') {
        throw new MastraError({
          id: createVectorErrorId('PG', 'BUILD_INDEX', 'UNSUPPORTED_INDEX_TYPE'),
          text: `IVFFlat indexes do not support sparsevec type. Use HNSW instead.`,
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName, vectorType, indexType },
        });
      }

      // Validate index type restrictions for bit + jaccard
      // IVFFlat supports bit vectors with Hamming distance only, not Jaccard
      if (indexType === 'ivfflat' && vectorType === 'bit' && metric === 'jaccard') {
        throw new MastraError({
          id: createVectorErrorId('PG', 'BUILD_INDEX', 'UNSUPPORTED_INDEX_TYPE'),
          text: `IVFFlat indexes do not support Jaccard distance for bit vectors. Use HNSW instead.`,
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName, vectorType, indexType, metric },
        });
      }

      const { tableName, vectorIndexName } = this.getTableName(indexName);

      // Try to get existing index info to check if configuration has changed
      let existingIndexInfo: PGIndexMetadata | null = null;
      let dimension = 0;
      try {
        existingIndexInfo = await this.describeIndexMetadata({ indexName }, client);
        dimension = existingIndexInfo.dimension;

        if (isConfigEmpty && existingIndexInfo.metric === metric) {
          if (existingIndexInfo.type === 'flat') {
            // No index exists - create the default ivfflat
            this.logger?.debug(`No index exists for ${vectorIndexName}, will create default ivfflat index`);
          } else {
            // Preserve existing non-flat index
            this.logger?.debug(
              `Index ${vectorIndexName} already exists (type: ${existingIndexInfo.type}, metric: ${existingIndexInfo.metric}), preserving existing configuration`,
            );
            const cacheKey = await this.getIndexCacheKey({
              indexName,
              dimension,
              type: existingIndexInfo.type,
              metric: existingIndexInfo.metric,
              vectorType: existingIndexInfo.vectorType,
            });
            this.createdIndexes.set(indexName, cacheKey);
            this.indexVectorTypes.set(indexName, existingIndexInfo.vectorType);
            return;
          }
        }

        // If config was empty but metric didn't match, OR config was provided, check for changes
        let configMatches = existingIndexInfo.metric === metric && existingIndexInfo.type === indexType;
        if (indexType === 'hnsw') {
          configMatches =
            configMatches &&
            existingIndexInfo.config.m === (indexConfig.hnsw?.m ?? 8) &&
            existingIndexInfo.config.efConstruction === (indexConfig.hnsw?.efConstruction ?? 32);
        } else if (indexType === 'flat') {
          configMatches = configMatches && existingIndexInfo.type === 'flat';
        } else if (indexType === 'ivfflat' && indexConfig.ivf?.lists) {
          configMatches = configMatches && existingIndexInfo.config.lists === indexConfig.ivf?.lists;
        }

        if (configMatches) {
          this.logger?.debug(`Index ${vectorIndexName} already exists with same configuration, skipping recreation`);
          // Update cache with the existing configuration
          const cacheKey = await this.getIndexCacheKey({
            indexName,
            dimension,
            type: existingIndexInfo.type,
            metric: existingIndexInfo.metric,
            vectorType: existingIndexInfo.vectorType,
          });
          this.createdIndexes.set(indexName, cacheKey);
          this.indexVectorTypes.set(indexName, existingIndexInfo.vectorType);
          return;
        }

        // Configuration changed, need to rebuild
        this.logger?.info(`Index ${vectorIndexName} configuration changed, rebuilding index`);
        await client.query(`DROP INDEX IF EXISTS ${vectorIndexName}`);
        this.invalidateIndexCaches(indexName);
      } catch {
        this.logger?.debug(`Index ${indexName} doesn't exist yet, will create it`);
      }

      if (indexType === 'flat') {
        this.invalidateIndexCaches(indexName);
        return;
      }

      // Set search path so vector operator classes (e.g. vector_cosine_ops) resolve correctly
      await this.ensureSearchPath(client);

      // Get the operator class based on vector type and metric
      // pgvector uses different operator classes for vector vs halfvec
      // Use the detected vectorType from existing table if available, otherwise use the parameter
      const effectiveVectorType = existingIndexInfo?.vectorType ?? vectorType;
      const metricOp = this.getVectorOps(effectiveVectorType, metric).operatorClass;

      let indexSQL: string;
      if (indexType === 'hnsw') {
        const m = indexConfig.hnsw?.m ?? 8;
        const efConstruction = indexConfig.hnsw?.efConstruction ?? 32;

        indexSQL = `
          CREATE INDEX IF NOT EXISTS ${vectorIndexName}
          ON ${tableName}
          USING hnsw (embedding ${metricOp})
          WITH (
            m = ${m},
            ef_construction = ${efConstruction}
          )
        `;
      } else {
        let lists: number;
        if (indexConfig.ivf?.lists) {
          lists = indexConfig.ivf.lists;
        } else {
          const size = (await client.query(`SELECT COUNT(*) FROM ${tableName}`)).rows[0].count;
          lists = Math.max(100, Math.min(4000, Math.floor(Math.sqrt(size) * 2)));
        }
        indexSQL = `
          CREATE INDEX IF NOT EXISTS ${vectorIndexName}
          ON ${tableName}
          USING ivfflat (embedding ${metricOp})
          WITH (lists = ${lists});
        `;
      }

      await client.query(indexSQL);
    });
  }

  private async createMetadataIndexes(tableName: string, indexName: string, metadataFields: string[]) {
    const hasher = await this.hasher;
    for (const field of metadataFields) {
      // Hash the field to produce a safe, fixed-length suffix for the index name.
      // This avoids issues with fields containing characters invalid in SQL identifiers
      // (e.g. "user-id") and keeps the total index name under PostgreSQL's 63-char limit.
      const fieldHash = hasher.h32(field).toString(16);
      const prefix = indexName.slice(0, 63 - '_md__idx'.length - fieldHash.length);
      const metadataIdxName = `"${prefix}_md_${fieldHash}_idx"`;
      // DDL statements don't support bind parameters, so we must interpolate
      // the field name as a literal. Escape single quotes to prevent SQL injection.
      const escapedField = field.replace(/'/g, "''");
      // Use CONCURRENTLY to avoid blocking writers on large existing tables.
      // This must run outside a transaction, so we use pool.query() directly.
      await this.pool.query(
        `
        CREATE INDEX CONCURRENTLY IF NOT EXISTS ${metadataIdxName}
        ON ${tableName} ((metadata->>'${escapedField}'))
      `,
      );
    }
  }

  private async installVectorExtension(client: pg.PoolClient) {
    // If we've already successfully installed, no need to do anything
    if (this.vectorExtensionInstalled) {
      return;
    }

    // If there's no existing installation attempt or the previous one failed
    if (!this.installVectorExtensionPromise) {
      this.installVectorExtensionPromise = (async () => {
        try {
          // First, detect if and where the extension is already installed
          const existingSchema = await this.detectVectorExtensionSchema(client);

          if (existingSchema) {
            this.vectorExtensionInstalled = true;
            this.vectorExtensionSchema = existingSchema;
            this.logger.info(`Vector extension already installed in schema: ${existingSchema}`);
            return;
          }

          // Try to install the extension
          try {
            // First try to install in the custom schema if provided
            if (this.schema && this.schema !== 'public') {
              try {
                await client.query(`CREATE EXTENSION IF NOT EXISTS vector SCHEMA ${this.getSchemaName()}`);
                // Re-detect to get the version info (needed for halfvec support check)
                const installedSchema = await this.detectVectorExtensionSchema(client);
                if (installedSchema) {
                  this.vectorExtensionInstalled = true;
                  this.logger.info(`Vector extension installed in schema: ${installedSchema}`);
                  return;
                }
                // Fallback if detection failed but install succeeded
                this.vectorExtensionInstalled = true;
                this.vectorExtensionSchema = this.schema;
                this.logger.info(`Vector extension installed in schema: ${this.schema}`);
                return;
              } catch (schemaError) {
                this.logger.debug(`Could not install vector extension in schema ${this.schema}, trying public schema`, {
                  error: schemaError,
                });
              }
            }

            // Fall back to installing in public schema (or default)
            await client.query('CREATE EXTENSION IF NOT EXISTS vector');

            // Detect where it was actually installed
            const installedSchema = await this.detectVectorExtensionSchema(client);
            if (installedSchema) {
              this.vectorExtensionInstalled = true;
              this.vectorExtensionSchema = installedSchema;
              this.logger.info(`Vector extension installed in schema: ${installedSchema}`);
            }
          } catch (error) {
            this.logger.warn(
              'Could not install vector extension. This requires superuser privileges. ' +
                'If the extension is already installed, you can ignore this warning.',
              { error },
            );

            // Even if installation failed, check if it exists somewhere
            const existingSchema = await this.detectVectorExtensionSchema(client);
            if (existingSchema) {
              this.vectorExtensionInstalled = true;
              this.vectorExtensionSchema = existingSchema;
              this.logger.info(`Vector extension found in schema: ${existingSchema}`);
            }
          }
        } catch (error) {
          this.logger.error('Error setting up vector extension', { error });
          this.vectorExtensionInstalled = undefined;
          this.installVectorExtensionPromise = null;
          throw error;
        } finally {
          this.installVectorExtensionPromise = null;
        }
      })();
    }

    await this.installVectorExtensionPromise;
  }

  async listIndexes(): Promise<string[]> {
    const client = await this.pool.connect();
    try {
      // Query for tables that match the exact Mastra PgVector table structure:
      // Must have: vector_id (TEXT), embedding (vector or halfvec), metadata (JSONB)
      // Without an explicit schemaName, look in every schema on the effective search_path
      // (current_schemas), which is where unqualified CREATE TABLE places tables.
      const mastraTablesQuery = `
        SELECT DISTINCT t.table_name
        FROM information_schema.tables t
        WHERE t.table_schema = ANY(
          CASE WHEN $1::text IS NULL THEN current_schemas(false) ELSE ARRAY[$1::text] END
        )
        AND EXISTS (
          SELECT 1
          FROM information_schema.columns c
          WHERE c.table_schema = t.table_schema
          AND c.table_name = t.table_name
          AND c.column_name = 'vector_id'
          AND c.data_type = 'text'
        )
        AND EXISTS (
          SELECT 1
          FROM information_schema.columns c
          WHERE c.table_schema = t.table_schema
          AND c.table_name = t.table_name
          AND c.column_name = 'embedding'
          AND c.udt_name IN ('vector', 'halfvec', 'bit', 'sparsevec')
        )
        AND EXISTS (
          SELECT 1
          FROM information_schema.columns c
          WHERE c.table_schema = t.table_schema
          AND c.table_name = t.table_name
          AND c.column_name = 'metadata'
          AND c.data_type = 'jsonb'
        );
      `;
      const mastraTables = await client.query(mastraTablesQuery, [this.schema ?? null]);
      return mastraTables.rows.map(row => row.table_name);
    } catch (e) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'LIST_INDEXES', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
        },
        e,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client.release();
    }
  }

  /**
   * Retrieves statistics about a vector index.
   *
   * @param {string} indexName - The name of the index to describe
   * @returns A promise that resolves to the index statistics including dimension, count and metric
   */
  async describeIndex({ indexName }: DescribeIndexParams): Promise<PGIndexStats> {
    const metadata = await this.describeIndexMetadata({ indexName });
    const count = await this.countIndexRows({ indexName });
    return { ...metadata, count };
  }

  /**
   * Reads the index metadata that lives in the Postgres catalog. Unlike
   * {@link describeIndex} it issues no `COUNT(*)`, so its cost does not grow with the
   * number of rows in the table.
   */
  private async describeIndexMetadata(
    { indexName }: DescribeIndexParams,
    existingClient?: pg.PoolClient,
  ): Promise<PGIndexMetadata> {
    const client = existingClient ?? (await this.pool.connect());
    try {
      const { tableName, parsedIndexName } = this.getTableName(indexName);

      // Check if table exists with a vector-type embedding column. Resolving through
      // to_regclass(tableName) mirrors how the unqualified DDL resolves via search_path
      // when no schemaName is configured (e.g. "$user" schemas).
      const tableExistsQuery = `
        SELECT t.typname AS udt_name
        FROM pg_attribute a
        JOIN pg_type t ON t.oid = a.atttypid
        WHERE a.attrelid = to_regclass($1)
          AND a.attname = 'embedding'
          AND a.attnum > 0
          AND NOT a.attisdropped
          AND t.typname IN ('vector', 'halfvec', 'bit', 'sparsevec')
        LIMIT 1;
      `;
      const tableExists = await client.query(tableExistsQuery, [tableName]);

      if (tableExists.rows.length === 0) {
        throw new Error(`Vector table ${tableName} does not exist`);
      }

      // Determine the vector type from the column
      const udtName = tableExists.rows[0].udt_name;
      const vectorType: VectorType =
        udtName === 'halfvec'
          ? 'halfvec'
          : udtName === 'bit'
            ? 'bit'
            : udtName === 'sparsevec'
              ? 'sparsevec'
              : 'vector';

      // Get vector dimension
      const dimensionQuery = `
                SELECT atttypmod as dimension
                FROM pg_attribute
                WHERE attrelid = $1::regclass
                AND attname = 'embedding';
            `;

      // Get index metric type
      const indexQuery = `
            SELECT
                am.amname as index_method,
                pg_get_indexdef(i.indexrelid) as index_def,
                opclass.opcname as operator_class
            FROM pg_index i
            JOIN pg_class c ON i.indexrelid = c.oid
            JOIN pg_am am ON c.relam = am.oid
            JOIN pg_opclass opclass ON i.indclass[0] = opclass.oid
            WHERE c.relname = $1
            AND i.indrelid = to_regclass($2);
            `;

      const dimResult = await client.query(dimensionQuery, [tableName]);
      const indexResult = await client.query(indexQuery, [`${parsedIndexName}_vector_idx`, tableName]);

      const { index_method, index_def, operator_class } = indexResult.rows[0] || {
        index_method: 'flat',
        index_def: '',
        operator_class: 'cosine',
      };

      // Convert pg_vector operator class to our metric type
      const metric: PgMetric = operator_class.includes('hamming')
        ? 'hamming'
        : operator_class.includes('jaccard')
          ? 'jaccard'
          : operator_class.includes('l2')
            ? 'euclidean'
            : operator_class.includes('ip')
              ? 'dotproduct'
              : 'cosine';

      // Parse index configuration
      const config: { m?: number; efConstruction?: number; lists?: number } = {};

      if (index_method === 'hnsw') {
        const m = index_def.match(/m\s*=\s*'?(\d+)'?/)?.[1];
        const efConstruction = index_def.match(/ef_construction\s*=\s*'?(\d+)'?/)?.[1];
        if (m) config.m = parseInt(m);
        if (efConstruction) config.efConstruction = parseInt(efConstruction);
      } else if (index_method === 'ivfflat') {
        const lists = index_def.match(/lists\s*=\s*'?(\d+)'?/)?.[1];
        if (lists) config.lists = parseInt(lists);
      }

      return {
        dimension: dimResult.rows[0].dimension,
        metric: metric as PGIndexStats['metric'],
        type: index_method as 'flat' | 'hnsw' | 'ivfflat',
        vectorType,
        config,
      };
    } catch (e: any) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'DESCRIBE_INDEX', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        e,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      if (!existingClient) client.release();
    }
  }

  /**
   * Exact row count for an index. This is a full scan of the table, so it is only issued
   * for the public {@link describeIndex}, never from an internal code path.
   */
  private async countIndexRows({ indexName }: DescribeIndexParams): Promise<number> {
    const client = await this.pool.connect();
    try {
      const { tableName } = this.getTableName(indexName);
      const countResult = await client.query(`
                SELECT COUNT(*) as count
                FROM ${tableName};
            `);
      return parseInt(countResult.rows[0].count);
    } catch (e: any) {
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'DESCRIBE_INDEX', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        e,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client.release();
    }
  }

  async deleteIndex({ indexName }: DeleteIndexParams): Promise<void> {
    await this.getMutexByName(`create-${indexName}`).runExclusive(async () => {
      const client = await this.pool.connect();
      try {
        const { tableName } = this.getTableName(indexName);
        // Drop the table
        await client.query(`DROP TABLE IF EXISTS ${tableName} CASCADE`);
        this.createdIndexes.delete(indexName);
        this.namespaceReadyIndexes.delete(indexName);
        this.namespaceReadyCache.delete(indexName);
        this.indexVectorTypes.delete(indexName);
        this.invalidateIndexCaches(indexName);
      } catch (error: any) {
        await client.query('ROLLBACK');
        const mastraError = new MastraError(
          {
            id: createVectorErrorId('PG', 'DELETE_INDEX', 'FAILED'),
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.THIRD_PARTY,
            details: {
              indexName,
            },
          },
          error,
        );
        this.logger?.trackException(mastraError);
        throw mastraError;
      } finally {
        client.release();
      }
    });
  }

  async truncateIndex({ indexName }: DeleteIndexParams): Promise<void> {
    const client = await this.pool.connect();
    try {
      const { tableName } = this.getTableName(indexName);
      await client.query(`TRUNCATE ${tableName}`);
    } catch (e: any) {
      await client.query('ROLLBACK');
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'TRUNCATE_INDEX', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
          },
        },
        e,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client.release();
    }
  }

  async disconnect() {
    // Wait for cache warmup to complete before closing pool
    // This prevents "Cannot use a pool after calling end on the pool" errors
    if (this.cacheWarmupPromise) {
      try {
        await this.cacheWarmupPromise;
      } catch {
        // Ignore errors - we're shutting down anyway
      }
    }

    await this.pool.end();
  }

  /**
   * Updates a vector by its ID with the provided vector and/or metadata.
   * @param indexName - The name of the index containing the vector.
   * @param id - The ID of the vector to update.
   * @param update - An object containing the vector and/or metadata to update.
   * @param update.vector - An optional array of numbers representing the new vector.
   * @param update.metadata - An optional record containing the new metadata.
   * @returns A promise that resolves when the update is complete.
   * @throws Will throw an error if no updates are provided or if the update operation fails.
   */
  async updateVector({
    indexName,
    id,
    filter,
    update,
    namespace = DEFAULT_NAMESPACE,
  }: PgUpdateVectorParams): Promise<void> {
    let client;
    try {
      if (!update.vector && !update.metadata) {
        throw new Error('No updates provided');
      }

      // Validate that exactly one of id or filter is provided
      if (!id && !filter) {
        throw new MastraError({
          id: createVectorErrorId('PG', 'UPDATE_VECTOR', 'NO_TARGET'),
          text: 'Either id or filter must be provided',
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName },
        });
      }

      if (id && filter) {
        throw new MastraError({
          id: createVectorErrorId('PG', 'UPDATE_VECTOR', 'MUTUALLY_EXCLUSIVE'),
          text: 'Cannot provide both id and filter - they are mutually exclusive',
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName },
        });
      }

      const indexInfo = await this.getIndexMetadata({ indexName });
      await this.ensureNamespaceReady(indexName);
      client = await this.pool.connect();
      // Set search path so vector type casts (e.g. ::vector, ::halfvec) resolve correctly
      await this.ensureSearchPath(client);

      const { tableName } = this.getTableName(indexName);

      // Get the properly qualified vector type for this index
      const qualifiedVectorType = this.getVectorTypeName(indexInfo.vectorType, indexInfo.dimension);
      const ops = this.getVectorOps(indexInfo.vectorType, indexInfo.metric ?? 'cosine');

      let updateParts = [];
      let values: any[] = [];
      let valueIndex = 1;

      // Build SET clause
      if (update.vector) {
        updateParts.push(`embedding = $${valueIndex}::${qualifiedVectorType}`);
        values.push(ops.formatVector(update.vector, indexInfo.dimension));
        valueIndex++;
      }

      if (update.metadata) {
        updateParts.push(`metadata = $${valueIndex}::jsonb`);
        values.push(JSON.stringify(update.metadata));
        valueIndex++;
      }

      if (updateParts.length === 0) {
        return;
      }

      let whereClause: string;
      let whereValues: any[];
      const namespaceIndex = valueIndex;
      values.push(namespace);
      valueIndex++;

      if (id) {
        // Update by ID
        whereClause = `namespace = $${namespaceIndex} AND vector_id = $${valueIndex}`;
        whereValues = [id];
      } else {
        // Update by filter
        if (!filter || Object.keys(filter).length === 0) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'UPDATE_VECTOR', 'EMPTY_FILTER'),
            text: 'Cannot update with empty filter',
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            details: { indexName },
          });
        }

        const translatedFilter = this.transformFilter(filter);
        const { sql: filterQuery, values: filterValues } = buildDeleteFilterQuery(translatedFilter);

        // Extract WHERE clause (remove "WHERE" prefix if present)
        whereClause = filterQuery.trim().replace(/^WHERE\s+/i, '');

        if (!whereClause) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'UPDATE_VECTOR', 'INVALID_FILTER'),
            text: 'Filter produced empty WHERE clause',
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            details: { indexName, filter: JSON.stringify(filter) },
          });
        }

        // Adjust parameter indices for filter values
        whereClause = whereClause.replace(/\$(\d+)/g, (match, num) => {
          const newIndex = parseInt(num) + valueIndex - 1;
          return `$${newIndex}`;
        });
        whereClause = `namespace = $${namespaceIndex} AND (${whereClause})`;
        whereValues = filterValues;
      }

      const query = `
        UPDATE ${tableName}
        SET ${updateParts.join(', ')}
        WHERE ${whereClause}
      `;

      const result = await client.query(query, [...values, ...whereValues]);

      this.logger?.info(`Updated ${result.rowCount || 0} vectors in ${indexName}`, {
        indexName,
        id: id ? id : undefined,
        filter: filter ? filter : undefined,
        updatedCount: result.rowCount || 0,
      });
    } catch (error: any) {
      if (error instanceof MastraError) {
        throw error;
      }

      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'UPDATE_VECTOR', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
            ...(id && { id }),
            ...(filter && { filter: JSON.stringify(filter) }),
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client?.release();
    }
  }

  /**
   * Deletes a vector by its ID.
   * @param indexName - The name of the index containing the vector.
   * @param id - The ID of the vector to delete.
   * @returns A promise that resolves when the deletion is complete.
   * @throws Will throw an error if the deletion operation fails.
   */
  async deleteVector({ indexName, id, namespace = DEFAULT_NAMESPACE }: PgDeleteVectorParams): Promise<void> {
    let client;
    try {
      await this.ensureNamespaceReady(indexName);
      client = await this.pool.connect();
      const { tableName } = this.getTableName(indexName);
      const query = `
        DELETE FROM ${tableName}
        WHERE vector_id = $1 AND namespace = $2
      `;
      await client.query(query, [id, namespace]);
    } catch (error: any) {
      if (error instanceof MastraError) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'DELETE_VECTOR', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
            id,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client?.release();
    }
  }

  /**
   * Delete vectors matching a metadata filter.
   * @param indexName - The name of the index containing the vectors.
   * @param filter - The filter to match vectors for deletion.
   * @returns A promise that resolves when the deletion is complete.
   * @throws Will throw an error if the deletion operation fails.
   */
  async deleteVectors({ indexName, filter, ids, namespace }: PgDeleteVectorsParams): Promise<void> {
    let client;
    const effectiveNamespace = namespace ?? DEFAULT_NAMESPACE;
    try {
      await this.ensureNamespaceReady(indexName);
      client = await this.pool.connect();
      const { tableName } = this.getTableName(indexName);

      // Validate that exactly one of filter or ids is provided
      if (!filter && !ids && namespace === undefined) {
        throw new MastraError({
          id: createVectorErrorId('PG', 'DELETE_VECTORS', 'NO_TARGET'),
          text: 'Either filter or ids must be provided, unless an explicit namespace is used',
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName },
        });
      }

      if (filter && ids) {
        throw new MastraError({
          id: createVectorErrorId('PG', 'DELETE_VECTORS', 'MUTUALLY_EXCLUSIVE'),
          text: 'Cannot provide both filter and ids - they are mutually exclusive',
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.USER,
          details: { indexName },
        });
      }

      let query: string;
      let values: any[];

      if (ids) {
        // Delete by IDs
        if (ids.length === 0) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'DELETE_VECTORS', 'EMPTY_IDS'),
            text: 'Cannot delete with empty ids array',
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            details: { indexName },
          });
        }

        const placeholders = ids.map((_: string, i: number) => `$${i + 1}`).join(', ');
        query = `DELETE FROM ${tableName} WHERE vector_id IN (${placeholders}) AND namespace = $${ids.length + 1}`;
        values = [...ids, effectiveNamespace];
      } else if (filter) {
        // Delete by filter
        // Safety check: Don't allow empty filters to prevent accidental deletion of all vectors
        if (!filter || Object.keys(filter).length === 0) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'DELETE_VECTORS', 'EMPTY_FILTER'),
            text: 'Cannot delete with empty filter. Use deleteIndex to delete all vectors.',
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            details: { indexName },
          });
        }

        // Translate filter using existing infrastructure
        const translatedFilter = this.transformFilter(filter);
        const { sql: filterQuery, values: filterValues } = buildDeleteFilterQuery(translatedFilter);

        // Extract WHERE clause (remove "WHERE" prefix if present)
        const whereClause = filterQuery.trim().replace(/^WHERE\s+/i, '');

        if (!whereClause) {
          throw new MastraError({
            id: createVectorErrorId('PG', 'DELETE_VECTORS', 'INVALID_FILTER'),
            text: 'Filter produced empty WHERE clause',
            domain: ErrorDomain.MASTRA_VECTOR,
            category: ErrorCategory.USER,
            details: { indexName, filter: JSON.stringify(filter) },
          });
        }

        query = `DELETE FROM ${tableName} WHERE namespace = $${filterValues.length + 1} AND (${whereClause})`;
        values = [...filterValues, effectiveNamespace];
      } else {
        query = `DELETE FROM ${tableName} WHERE namespace = $1`;
        values = [effectiveNamespace];
      }

      // Execute the delete query
      const result = await client.query(query, values);

      this.logger?.info(`Deleted ${result.rowCount || 0} vectors from ${indexName}`, {
        indexName,
        filter: filter ? filter : undefined,
        ids: ids ? ids : undefined,
        deletedCount: result.rowCount || 0,
      });
    } catch (error: any) {
      // Re-throw MastraErrors as-is
      if (error instanceof MastraError) {
        throw error;
      }

      // Wrap other errors
      const mastraError = new MastraError(
        {
          id: createVectorErrorId('PG', 'DELETE_VECTORS', 'FAILED'),
          domain: ErrorDomain.MASTRA_VECTOR,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            indexName,
            ...(filter && { filter: JSON.stringify(filter) }),
            ...(ids && { idsCount: ids.length }),
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      throw mastraError;
    } finally {
      client?.release();
    }
  }
}
